import React, { useEffect, useState, useRef } from "react";
import './App.css';
import Login from './components/login/login';
import Navbar from './components/navbar/navbar';
import Profile from './components/profile/profile';
import { Route, Routes, useLocation, Navigate, useNavigate } from 'react-router-dom';
import Dashboard from './components/dashboard/dashboard';
import PatientRecords from './components/patientRecords/patientRecords';
import PatientScreeningWrapper from './components/patientScreeningWrapper/patientScreeningWrapper';
import SyncData from './components/syncData/syncData';
import axios from 'axios';
import config from './config';
import ReportsComponent from "./components/reportsComponent/reportsComponent";
import SplashScreen from "./components/splashScreen/splashScreen";
import MLTestingPage from "./components/mlTesting/MLTestingPage";
import { Snackbar, Box, Typography, Paper, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudOffOutlined from '@mui/icons-material/CloudOffOutlined';
import WifiOffOutlined from '@mui/icons-material/WifiOffOutlined';
// Removed unused MUI imports
import useSyncValidation from './hooks/useSyncValidation';
import SyncWarningDialog from './components/syncWarningDialog/syncWarningDialog';
import { clearStoredCredentials } from './db';
import { cancelModelDownload, invalidateModelDownloadApiCache } from './services/modelService';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import {
  captureAppError,
  isAuthRedirectAxiosError,
  recordAuthRedirectBreadcrumb,
  shouldCaptureInGlobalAxiosInterceptor,
} from './utils/captureAppError';
import { terminateOpenCVWorker } from './services/opencvWorkerManager';
import { VERSION } from './version';

// Add axios interceptor to handle authentication errors
// Note: This runs outside React context, so we use window.history for client-side navigation
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login for actual authentication errors, not model download failures
    if (isAuthRedirectAxiosError(error)) {
      invalidateModelDownloadApiCache();
      localStorage.clear();
      window.history.replaceState({}, "", "/login");
      window.dispatchEvent(new PopStateEvent("popstate"));
      recordAuthRedirectBreadcrumb(error);
    } else if (shouldCaptureInGlobalAxiosInterceptor(error)) {
      captureAppError(error, {
        tags: {
          source: 'axios_interceptor',
          errorType: `http_${error.response.status}`,
        },
      });
    }
    return Promise.reject(error);
  }
);

const ProtectedRoute = ({
  element: Element,
  userLoggedIn,
  setUserLoggedIn,
  userData,
  restrictedForOphthalmologist = false
}) => {
  if (!userLoggedIn) {
    return (
      <Login userLoggedIn={userLoggedIn} setUserLoggedIn={setUserLoggedIn} />
    );
  }

  // Check if route is restricted for ophthalmologists
  if (restrictedForOphthalmologist) {
    const optometristRole = localStorage.getItem("optometrist_role");
    if (optometristRole === "Ophthalmologist") {
      // Redirect ophthalmologists to patient records page
      return <Navigate to="/patientrecord" replace />;
    }
  }

  return <Element userData={userData} setUserLoggedIn={setUserLoggedIn} />;
};

function App() {
  let userData = null;
  try {
    userData = JSON.parse(localStorage.getItem("user_profile"));
  } catch (e) {
    localStorage.removeItem("user_profile");
  }
  const token = localStorage.getItem("optha-token");
  const header = { "Authorization": "Token " + token };
  const patient = localStorage.getItem('patient_id');
  const idexists = !!patient;
  
  // Improved token validation
  const isNotToken = !token || token === "" || token === "null" || token === "undefined" || token === "undefined";
  
  const [userLoggedIn, setUserLoggedIn] = useState(!isNotToken);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // 👇 internet status handling
  // On login page, always assume online (don't show offline messages)
  const isLoginPage = location.pathname === '/login' || location.pathname === '/';
  const [isOnline, setIsOnline] = useState(isLoginPage ? true : navigator.onLine);
  /** null | 'offline' | 'backend' — snackbar message type */
  const [connectivityAlert, setConnectivityAlert] = useState(null);

  // 👇 sync validation
  const {
    syncWarning,
    syncBlock,
    isChecking,
    canAddNewData,
    clearSyncWarning,
    clearSyncBlock,
    refreshSyncStatus
  } = useSyncValidation();

  // Check if user is optometrist (only optometrists should see sync warnings)
  const optometristRole = localStorage.getItem("optometrist_role");
  const isOptometrist = optometristRole === "Optometrist";

  // 👇 Cleanup orphaned reports on app startup (runs once when user is logged in)
  useEffect(() => {
    const cleanupOrphanedData = async () => {
      if (!userLoggedIn || isLoginPage) return; // Only cleanup if user is logged in
      
      try {
        const { cleanupOrphanedReports, getCurrentUserId } = await import('./db');
        const currentUserId = getCurrentUserId();
        
        if (currentUserId) {
          await cleanupOrphanedReports(currentUserId);
        }
      } catch (error) {
        // Don't show error to user - this is background cleanup
      }
    };
    
    cleanupOrphanedData();
  }, [userLoggedIn, isLoginPage]);

  // 👇 Service worker update banner (no auto-reload).
  // `swUpdateReady` can fire from register() on `load` before this effect runs — attach listener then
  // re-check `registration.waiting` so refresh does not drop the banner on fast/slow devices.
  useEffect(() => {
    const onUpdateReady = () => setShowUpdateBanner(true);
    window.addEventListener('swUpdateReady', onUpdateReady);

    let cancelled = false;
    (async () => {
      if (!('serviceWorker' in navigator)) return;
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!cancelled && reg?.waiting) {
          setShowUpdateBanner(true);
        }
      } catch {
        // ignore
      }
    })();

    return () => {
      cancelled = true;
      window.removeEventListener('swUpdateReady', onUpdateReady);
    };
  }, []);

  // 👇 Effect to monitor connectivity: device offline vs backend unreachable
  const wasDeviceOfflineRef = useRef(false);
  const wasBackendUnreachableRef = useRef(false);

  useEffect(() => {
    const checkConnectionStatus = async () => {
      if (isLoginPage) return;

      const { getConnectivitySnapshot } = await import('./utils/offlineDetection');
      const snap = await getConnectivitySnapshot();

      setIsOnline(!snap.deviceOffline);

      if (snap.deviceOffline) {
        if (!wasDeviceOfflineRef.current) {
          setConnectivityAlert('offline');
        }
        wasDeviceOfflineRef.current = true;
        wasBackendUnreachableRef.current = false;
      } else {
        wasDeviceOfflineRef.current = false;
        if (snap.backendUnreachable) {
          if (!wasBackendUnreachableRef.current) {
            setConnectivityAlert('backend');
          }
          wasBackendUnreachableRef.current = true;
        } else {
          wasBackendUnreachableRef.current = false;
        }
      }
    };

    checkConnectionStatus();
    const interval = setInterval(checkConnectionStatus, 10000);

    const handleOnline = async () => {
      const { clearOfflineCache } = await import('./utils/offlineDetection');
      clearOfflineCache();
      wasDeviceOfflineRef.current = false;
      await checkConnectionStatus();
    };

    const handleOffline = () => {
      if (!isLoginPage) {
        setIsOnline(false);
        if (!wasDeviceOfflineRef.current) {
          setConnectivityAlert('offline');
        }
        wasDeviceOfflineRef.current = true;
        wasBackendUnreachableRef.current = false;
      }
      checkConnectionStatus();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isLoginPage]);
  // Handle authentication state changes
  useEffect(() => {
    const checkAuthStatus = () => {
      const currentToken = localStorage.getItem("optha-token");
      const currentUserData = localStorage.getItem("user_profile");
      
      const hasValidToken = currentToken && 
                           currentToken !== "" && 
                           currentToken !== "null" && 
                           currentToken !== "undefined";
      
      const hasValidUserData = currentUserData && currentUserData !== "null";
      
      if (hasValidToken && hasValidUserData) {
        setUserLoggedIn(true);
      } else {
        setUserLoggedIn(false);
        // Clear sync warnings/blocks when user logs out
        clearSyncWarning();
        clearSyncBlock();
        // Clear any invalid data
        if (!hasValidToken) localStorage.removeItem("optha-token");
        if (!hasValidUserData) localStorage.removeItem("user_profile");
      }
    };

    // Check immediately
    checkAuthStatus();

    // Listen for storage changes (when user logs in/out from other tabs)
    window.addEventListener('storage', checkAuthStatus);
    
    return () => {
      window.removeEventListener('storage', checkAuthStatus);
    };
  }, [clearSyncWarning, clearSyncBlock]);


  // Cleanup effect to cancel any pending model downloads when component unmounts
  useEffect(() => {
    const handleBeforeUnload = () => {
      cancelModelDownload();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also cancel on cleanup
      cancelModelDownload();
    };
  }, []);

  // Function to determine where to redirect logged-in users based on role
  const getRedirectPath = () => {
    if (!userLoggedIn) return null;
    
    const optometristRole = localStorage.getItem("optometrist_role");
    if (optometristRole === "Ophthalmologist") {
      return "/patientrecord";
    } else {
      return "/splashscreen";
    }
  };

  // Logout function that clears both localStorage and IndexedDB
  // @param {{ forServiceWorkerUpdate?: boolean }} opts — when true, do not restore lastVersion so the next full reload can set it from the new bundle.
  const handleLogout = async (opts = {}) => {
    const forServiceWorkerUpdate = opts.forServiceWorkerUpdate === true;
    try {
      terminateOpenCVWorker();
      invalidateModelDownloadApiCache();
      // Preserve version-related items to prevent reload loops (skip lastVersion on SW update path)
      const lastVersion = forServiceWorkerUpdate ? null : localStorage.getItem('lastVersion');
      const reloadedForSW = localStorage.getItem('reloadedForSW');
      
      // Clear localStorage
      localStorage.clear();

      // SPA navigation does not re-run index.js — re-seed lastVersion after SW update logout.
      if (forServiceWorkerUpdate) {
        try {
          const v =
            (typeof window !== 'undefined' && window.APP_VERSION != null && window.APP_VERSION !== '')
              ? String(window.APP_VERSION)
              : String(VERSION);
          localStorage.setItem('lastVersion', v);
        } catch (_) {}
      }
      
      // Restore version items (prevent service worker reload loops)
      if (lastVersion) localStorage.setItem('lastVersion', lastVersion);
      if (reloadedForSW) localStorage.setItem('reloadedForSW', reloadedForSW);
      
      // Clear stored credentials from IndexedDB
      await clearStoredCredentials();
      
      // Update login state
      setUserLoggedIn(false);
      
      // ✅ Use client-side navigation (NO HARD RELOAD) - works offline
      navigate("/login", { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
      terminateOpenCVWorker();
      invalidateModelDownloadApiCache();
      const lastVersion = forServiceWorkerUpdate ? null : localStorage.getItem('lastVersion');
      const reloadedForSW = localStorage.getItem('reloadedForSW');
      
      // Still clear localStorage and redirect even if IndexedDB clear fails
      localStorage.clear();

      if (forServiceWorkerUpdate) {
        try {
          const v =
            (typeof window !== 'undefined' && window.APP_VERSION != null && window.APP_VERSION !== '')
              ? String(window.APP_VERSION)
              : String(VERSION);
          localStorage.setItem('lastVersion', v);
        } catch (_) {}
      }
      
      if (lastVersion) localStorage.setItem('lastVersion', lastVersion);
      if (reloadedForSW) localStorage.setItem('reloadedForSW', reloadedForSW);
      
      setUserLoggedIn(false);
      // ✅ Use client-side navigation (NO HARD RELOAD) - works offline
      navigate("/login", { replace: true });
    }
  };

  return (
    <div className="App">
      {/* Show Navbar when logged in (except splashscreen) OR on login page — same header as after login; on login page hide menu/hamburger */}
      {(userLoggedIn && location.pathname !== "/splashscreen") || location.pathname === "/login" || location.pathname === "/" ? (
        <Navbar showMenu={userLoggedIn && location.pathname !== "/splashscreen" && location.pathname !== "/login" && location.pathname !== "/"} />
      ) : null}

      {/* Persistent offline banner – same design on all pages, shown until back online */}
      {!isLoginPage && !isOnline && (
        <Box
          sx={{
            width: '100%',
            py: 1.25,
            px: 2,
            backgroundColor: '#6B7280',
            color: '#fff',
            textAlign: 'center',
            zIndex: 9998,
          }}
        >
          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 400 }}>
            You are using MadhuNetrAI offline
          </Typography>
        </Box>
      )}

      {/* Update available banner – not on splash (splash calls applyUpdate itself when a worker is waiting). */}
      {showUpdateBanner && userLoggedIn && location.pathname !== "/splashscreen" && (
        <Box
          sx={{
            width: '100%',
            py: 1.25,
            px: 2,
            backgroundColor: '#059669',
            color: '#fff',
            textAlign: 'center',
            zIndex: 9998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 400 }}>
            New version available
          </Typography>
          <Typography
            component="button"
            onClick={async () => {
              setShowUpdateBanner(false);
              await handleLogout({ forServiceWorkerUpdate: true });
              serviceWorkerRegistration.applyUpdate();
            }}
            sx={{
              fontSize: '0.9375rem',
              fontWeight: 600,
              color: '#fff',
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
              cursor: 'pointer',
              '&:hover': { background: 'rgba(255,255,255,0.3)' },
            }}
          >
            Update App
          </Typography>
        </Box>
      )}
      
      <Routes>
        <Route path='/dashboard' element={
          <ProtectedRoute element={Dashboard} userLoggedIn={userLoggedIn} setUserLoggedIn={setUserLoggedIn} restrictedForOphthalmologist={true} />
        } />
        <Route path='/account' element={
          <ProtectedRoute element={Profile} userLoggedIn={userLoggedIn} setUserLoggedIn={setUserLoggedIn} userData={userData} />
        } />
        <Route path='/patientscreen' element={
          <ProtectedRoute element={PatientScreeningWrapper} userLoggedIn={userLoggedIn} setUserLoggedIn={setUserLoggedIn} userData={userData} />
        } />
        <Route path='/patientrecord' element={
          <ProtectedRoute element={PatientRecords} userLoggedIn={userLoggedIn} setUserLoggedIn={setUserLoggedIn} />
        } />
        <Route path='/syncdata' element={
          <ProtectedRoute element={SyncData} userLoggedIn={userLoggedIn} setUserLoggedIn={setUserLoggedIn} restrictedForOphthalmologist={true} />
        } />
        <Route path='/patientrecord/:patientrecordId' element={
          <ProtectedRoute element={ReportsComponent} userLoggedIn={userLoggedIn} setUserLoggedIn={setUserLoggedIn} userData={userData} />
        } />
        <Route path='/splashscreen' element={
          <ProtectedRoute element={SplashScreen} userLoggedIn={userLoggedIn} setUserLoggedIn={setUserLoggedIn} userData={userData} />
        } />
        <Route path='/ml-testing' element={
          <ProtectedRoute element={MLTestingPage} userLoggedIn={userLoggedIn} setUserLoggedIn={setUserLoggedIn} />
        } />
        <Route path='/login' element={
          <Login userLoggedIn={userLoggedIn} setUserLoggedIn={setUserLoggedIn} />
        } />
        <Route path='/' element={
          userLoggedIn ? (
            <Navigate to={getRedirectPath()} replace />
          ) : (
            <Login userLoggedIn={userLoggedIn} setUserLoggedIn={setUserLoggedIn} />
          )
        } />
      </Routes>

      {/* Connectivity toast: bottom-center card (does not cover header) */}
      {!isLoginPage && (
        <Snackbar
          open={connectivityAlert !== null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          autoHideDuration={connectivityAlert === 'backend' ? 8000 : 5500}
          onClose={(_, reason) => {
            if (reason === 'clickaway') return;
            setConnectivityAlert(null);
          }}
          sx={{
            zIndex: 9999,
            bottom: { xs: 20, sm: 28 },
            left: '50% !important',
            right: 'auto !important',
            transform: 'translateX(-50%)',
            maxWidth: { xs: 'calc(100vw - 24px)', sm: 460 },
          }}
        >
          <Paper
            elevation={8}
            sx={{
              width: '100%',
              maxWidth: 440,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor:
                connectivityAlert === 'offline'
                  ? 'rgba(217, 119, 6, 0.28)'
                  : 'rgba(13, 30, 131, 0.14)',
              bgcolor: connectivityAlert === 'offline' ? 'rgba(255, 251, 235, 0.98)' : '#ffffff',
              boxShadow:
                connectivityAlert === 'offline'
                  ? '0 10px 40px rgba(180, 83, 9, 0.1)'
                  : '0 10px 40px rgba(15, 23, 42, 0.1)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.75,
                pl: 2,
                pr: 1,
                py: 2,
                borderLeft: '4px solid',
                borderLeftColor: connectivityAlert === 'offline' ? '#d97706' : '#0D1E83',
              }}
            >
              {connectivityAlert === 'offline' ? (
                <WifiOffOutlined
                  sx={{ color: '#b45309', fontSize: 24, flexShrink: 0, mt: 0.125 }}
                />
              ) : (
                <CloudOffOutlined
                  sx={{ color: '#0D1E83', fontSize: 24, flexShrink: 0, mt: 0.125 }}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0, pt: 0.125 }}>
                <Typography
                  component="div"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    lineHeight: 1.35,
                    color: 'text.primary',
                    letterSpacing: '-0.01em',
                    mb: 0.75,
                  }}
                >
                  {connectivityAlert === 'offline'
                    ? "You're offline"
                    : "Can't reach the server"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.8125rem',
                    lineHeight: 1.55,
                    color: 'text.secondary',
                    fontWeight: 400,
                  }}
                >
                  {connectivityAlert === 'offline'
                    ? 'Screening can continue. Sync when your connection is restored.'
                    : 'Usually temporary. You can keep screening locally; sync will work again when the service is back.'}
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label="Dismiss notification"
                onClick={() => setConnectivityAlert(null)}
                sx={{
                  color: 'text.secondary',
                  flexShrink: 0,
                  mt: -0.25,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        </Snackbar>
      )}

      {/* 👇 Sync Warning/Block Dialog - Only show if user is logged in and is optometrist */}
      {userLoggedIn && isOptometrist && (
        <SyncWarningDialog
          open={!!syncWarning || !!syncBlock}
          onClose={syncWarning ? clearSyncWarning : clearSyncBlock}
          warningData={syncWarning || syncBlock}
          isBlocking={!!syncBlock}
          onProceed={() => {
            // User chose to continue despite sync warning
          }}
        />
      )}

    </div>
  );
}

export default App;
